// The line (module 10 U353, U356, U357, U366; pipeline RC5 draws it at the head of the rundown, RC6 on every surface
// that names a company). Who the company is to us, who handles them, what is in force, what was billed, what is open,
// in the server's words (apps/api/src/copilot/rundown.ts shapeLine). No middle dots: spacing separates the parts, and an
// empty part is said in words. The name opens the record and covers the row, so the whole line opens it; a brand or a
// parent is a link of its own above it.
import type { MouseEvent, ReactNode } from 'react';
import type { CompanyLineView, LinePart, Plate } from './api.js';
import './company.css';

/** How a link opens: inside the portal through its router, or, in a window that is not the portal, in a new tab. */
export type Go = ((path: string) => void) | null;

/**
 * The portal's own way to a page, for a surface that is not handed App.tsx's `go`: the address moves and App follows it
 * on popstate (App.tsx useSurface), as the back button does.
 */
export function inPortal(path: string): void {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
}

export function Lnk({ to, go, className, children }: { to: string; go: Go; className?: string; children: ReactNode }) {
  const onClick = go ? (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    go(to);
  } : undefined;
  return go
    ? <a className={className} href={to} onClick={onClick}>{children}</a>
    : <a className={className} href={to} target="_blank" rel="noreferrer">{children}</a>;
}

/** A date in a plate; the dot is red, and only for an agreement within 90 days of its end or a date gone by. */
export function PlateOf({ plate }: { plate: Plate }) {
  return <span className={plate.red ? 'rd-plate red' : 'rd-plate'}>{plate.red ? <i aria-hidden="true" /> : null}{plate.text}</span>;
}

function Part({ part }: { part: LinePart }) {
  if (part.none) return <span className="g"><span className="none">{part.text}</span></span>;
  // "Handled by Will Fyler" puts its words first; the others put the figure first ("£82,357 billed in 12 months").
  const wordsFirst = part.text === 'Handled by';
  return (
    <span className="g">
      <span>
        {wordsFirst ? <>{part.text} <b>{part.bold}</b></> : <><b>{part.bold}</b>{part.text.startsWith(',') ? part.text : part.text ? ` ${part.text}` : ''}</>}
      </span>
      {part.plate ? <PlateOf plate={part.plate} /> : null}
    </span>
  );
}

/**
 * The company's logo (module 10 U359, U371): the one the record keeps (pipeline RC7), or, until it keeps one or when
 * none was found, the initials of its name. Beside a name, never instead of it, so it says nothing a reader needs.
 */
export function Logo({ name, logo, size = 'md' }: { name: string; logo?: string | null; size?: 'sm' | 'md' }) {
  const cls = size === 'sm' ? 'co-logo sm' : 'co-logo';
  if (logo) return <img className={cls} src={logo} alt="" aria-hidden="true" />;
  return <span className={cls} aria-hidden="true">{initialsOf(name)}</span>;
}

const SUFFIX = /^(limited|ltd\.?|plc|llc|gmbh|inc\.?|s\.?a\.?|b\.?v\.?|the)$/i;

/** "GS" for Genius Sports, "B" for Betgenius, "WH" for William Hill Ltd: the first letter of up to two words. */
export function initialsOf(name: string): string {
  const words = name.split(/[\s/&,-]+/).filter((w) => /^[\p{L}\p{N}]/u.test(w) && !SUFFIX.test(w));
  const picked = (words.length ? words : [name.trim() || '?']).slice(0, 2);
  return picked.map((w) => [...w][0]!.toUpperCase()).join('');
}

/**
 * A company's name, wherever a page prints one, as a link to its record (BUGS.md X30; U356: "Any company name, anywhere,
 * opens the record"). It reads as the text around it until it is pointed at. A page handed App's `go` passes it; one
 * that is not moves through the portal's own router.
 */
export function CompanyName({ id, name, go = inPortal, className }: { id: string; name: string; go?: Go | undefined; className?: string }) {
  return <Lnk className={className ? `co-name ${className}` : 'co-name'} to={`/companies/${id}`} go={go}>{name}</Lnk>;
}

export function CompanyLine({ line, go, size = 'md', showName = true }: { line: CompanyLineView; go: Go; size?: 'sm' | 'md'; showName?: boolean }) {
  const record = `/companies/${line.id}`;
  return (
    <div className={size === 'sm' ? 'cl-line sm' : 'cl-line'}>
      <span className="g">
        {showName ? <><Logo name={line.name} logo={line.logo ?? null} size={size} /><Lnk className="nm" to={record} go={go}>{line.name}</Lnk></> : null}
        {line.brands.length > 0
          ? <span className="br-list">with {line.brands.map((b, i) => <span key={b.id}>{i > 0 ? ', ' : ''}<Lnk className="br" to={`/companies/${b.id}`} go={go}>{b.name}</Lnk></span>)}</span>
          : null}
        {line.kind ? <span className="cl-chip">{line.kind}</span> : null}
        {line.parent ? <span className="br-list">Brand of <Lnk className="br" to={`/companies/${line.parent.id}`} go={go}>{line.parent.name}</Lnk></span> : null}
        {line.handledBy.none ? <span className="none">{line.handledBy.text}</span> : <span>{line.handledBy.text} <b>{line.handledBy.bold}</b></span>}
      </span>
      <Part part={line.agreements} />
      <Part part={line.billed} />
      <Part part={line.deals} />
    </div>
  );
}

/**
 * Where a page names a company and draws its line (U353): the name, a link to the record, at once, and the whole line
 * in its place once it is read. A company with no line to show (one the person may not read) keeps just its name.
 */
export function LineOrName({ id, name, line, go = inPortal, size = 'sm' }: { id: string; name: string; line: CompanyLineView | null | undefined; go?: Go; size?: 'sm' | 'md' }) {
  if (line) return <CompanyLine line={line} go={go} size={size} />;
  return (
    <div className={size === 'sm' ? 'cl-line sm' : 'cl-line'}>
      <span className="g"><Logo name={name} size={size} /><Lnk className="nm" to={`/companies/${id}`} go={go}>{name}</Lnk></span>
    </div>
  );
}
